import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import t from '@guestyci/localize/t.macro';
import {
  Button,
  Input,
  Text,
  Heading,
  Stack,
  Checkbox,
  Label,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  createSchema,
  required,
} from '@guestyci/arc';
import { Eye, EyeOff } from '@guestyci/icons/system';
import { utils } from 'utils';

import EmailDisplay from './EmailDisplay';
import SocialLogin from './SocialLogin';
import HostsLink from './HostsLink';
import RegistrationLink from './RegistrationLink';
import ForgotHelpLinks from './ForgotHelpLinks';

const passwordFormSchema = createSchema({
  password: required(),
});

const PasswordStep = ({
  email,
  onChangeEmail,
  onPasswordSubmit,
  onGoogleSignIn,
  error: externalError,
  isSubmitting,
  rememberMe,
  onRememberMeChange,
  showGoogleLogin = true,
  isWidgetRendered = true,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: {
      password: '',
    },
    mode: 'onSubmit',
  });

  const handleSubmit = (data) => {
    onPasswordSubmit(data.password);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Stack spacing={6} direction="column" className="gst-pt-6 lg:gst-pt-0">
      <Stack spacing={2} direction="column">
        <Heading variant="h1">{t('Welcome!')}</Heading>
        <Text type="secondary" size="base">
          {t('Please enter your details to sign in.')}
        </Text>
      </Stack>

      <EmailDisplay
        email={email}
        onChangeEmail={onChangeEmail}
        disabled={isSubmitting}
      />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <input
            type="email"
            name="username"
            value={email || ''}
            autoComplete="username"
            readOnly
            style={{ display: 'none' }}
            aria-hidden="true"
          />

          <Stack spacing={6} direction="column">
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('Password')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      data-testid="signin-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('Enter your password')}
                      autoFocus
                      autoComplete="current-password"
                      disabled={isSubmitting}
                      suffix={
                          <button
                            type="button"
                            onClick={togglePasswordVisibility}
                            className="gst-p-0 gst-border-0 gst-bg-transparent gst-cursor-pointer"
                            style={{ opacity: 0.7 }}
                            aria-label={showPassword ? t('Hide password') : t('Show password')}
                          >
                          {showPassword ? (
                            <EyeOff className="gst-h-4 gst-w-4 gst-opacity-50" />
                          ) : (
                            <Eye className="gst-h-4 gst-w-4 gst-opacity-50" />
                          )}
                        </button>
                      }
                    />
                  </FormControl>
                  <FormMessage />
                  {externalError && !form.formState.errors.password && (
                    <Text type="error" size="sm">
                      {externalError}
                    </Text>
                  )}
                </FormItem>
              )}
            />

            <Stack spacing={2} direction="row" align="center">
              <Checkbox
                id="remember-me-password"
                checked={rememberMe}
                onCheckedChange={onRememberMeChange}
                disabled={isSubmitting}
              />
              <Label htmlFor="remember-me-password" className="gst-cursor-pointer">
                {t('Remember me')}
              </Label>
            </Stack>

            <Button
              data-testid="signin-submit"
              type="submit"
              variant="default"
              className="gst-w-full"
              isLoading={isSubmitting}
              disabled={isSubmitting}
            >
              {t('Continue')}
            </Button>
          </Stack>
        </form>
      </Form>

      {!utils.isHQDomain && !utils.isAcademyDomain && (
        <SocialLogin
          onGoogleSignIn={onGoogleSignIn}
          disabled={isSubmitting || !isWidgetRendered}
          showGoogle={showGoogleLogin}
        />  
      )}

      <ForgotHelpLinks disabled={isSubmitting} />

      {!utils.isHQDomain && !utils.isAcademyDomain && (
        <Stack spacing={2} direction="column" className="gst-pt-2">
          <RegistrationLink />
          <HostsLink />
        </Stack>
      )}
    </Stack>
  );
};

export default PasswordStep;
